import React from 'react';

/**
 * Handcuff Lock SVG
 * Two interlocking cuff rings (steel) connected by a short chain in the middle.
 * The right ring holds a glowing keyhole at its centre. When `unlocked` is true,
 * the rings rotate open and the chain stretches/fades.
 */
export default function HandcuffLock({ unlocked = false, attempt = 0 }) {
  return (
    <div
      data-testid="handcuff-lock"
      className="relative mx-auto"
      style={{
        width: 360,
        maxWidth: '92vw',
        filter: unlocked
          ? 'drop-shadow(0 0 28px rgba(255,210,120,0.85))'
          : 'drop-shadow(0 0 14px rgba(255,180,80,0.45))',
        transition: 'filter 800ms ease',
      }}
    >
      <svg
        viewBox="0 0 600 280"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="steel" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3a2a14" />
            <stop offset="35%" stopColor="#caa05a" />
            <stop offset="55%" stopColor="#f6dca0" />
            <stop offset="75%" stopColor="#9a6f30" />
            <stop offset="100%" stopColor="#1d1408" />
          </linearGradient>
          <linearGradient id="steelInner" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0a0703" />
            <stop offset="100%" stopColor="#211608" />
          </linearGradient>
          <radialGradient id="keyholeGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffe6a4" stopOpacity="1" />
            <stop offset="60%" stopColor="#c88a30" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <filter id="cuffGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Chain link group (centred) */}
        <g
          style={{
            transformOrigin: '300px 140px',
            transition: 'transform 700ms ease, opacity 700ms ease',
            transform: unlocked ? 'scaleX(1.35)' : 'scaleX(1)',
            opacity: unlocked ? 0.25 : 1,
          }}
        >
          {[0, 1, 2].map((i) => (
            <ellipse
              key={i}
              cx={262 + i * 26}
              cy={140}
              rx="14"
              ry="9"
              fill="none"
              stroke="url(#steel)"
              strokeWidth="4"
            />
          ))}
        </g>

        {/* LEFT cuff — rotates outward when unlocked */}
        <g
          style={{
            transformOrigin: '180px 140px',
            transition: 'transform 900ms cubic-bezier(.6,.05,.2,1)',
            transform: unlocked ? 'rotate(-22deg) translate(-14px, -4px)' : 'rotate(0deg)',
          }}
          filter="url(#cuffGlow)"
        >
          <circle cx="180" cy="140" r="92" fill="none" stroke="url(#steel)" strokeWidth="22" />
          <circle cx="180" cy="140" r="92" fill="none" stroke="#1b1206" strokeWidth="2" />
          <circle cx="180" cy="140" r="68" fill="none" stroke="url(#steelInner)" strokeWidth="14" />
          {/* rivets */}
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const r = 92;
            const rad = (deg * Math.PI) / 180;
            const cx = 180 + r * Math.cos(rad);
            const cy = 140 + r * Math.sin(rad);
            return <circle key={deg} cx={cx} cy={cy} r="3" fill="#f6dca0" />;
          })}
        </g>

        {/* RIGHT cuff — holds the keyhole, rotates outward when unlocked */}
        <g
          style={{
            transformOrigin: '420px 140px',
            transition: 'transform 900ms cubic-bezier(.6,.05,.2,1)',
            transform: unlocked ? 'rotate(22deg) translate(14px, -4px)' : 'rotate(0deg)',
          }}
          filter="url(#cuffGlow)"
        >
          <circle cx="420" cy="140" r="92" fill="none" stroke="url(#steel)" strokeWidth="22" />
          <circle cx="420" cy="140" r="92" fill="none" stroke="#1b1206" strokeWidth="2" />
          <circle cx="420" cy="140" r="68" fill="none" stroke="url(#steelInner)" strokeWidth="14" />

          {/* keyhole housing */}
          <circle cx="420" cy="140" r="34" fill="#0a0703" stroke="url(#steel)" strokeWidth="3" />
          <circle cx="420" cy="140" r="34" fill="url(#keyholeGlow)" opacity={unlocked ? 0.2 : 0.85} />
          {/* keyhole shape */}
          <g style={{ transition: 'transform 600ms ease', transform: unlocked ? 'rotate(90deg)' : 'rotate(0deg)', transformOrigin: '420px 140px' }}>
            <circle cx="420" cy="132" r="6" fill="#0a0703" />
            <rect x="417" y="132" width="6" height="18" fill="#0a0703" />
          </g>

          {/* rivets */}
          {[0, 60, 120, 180, 240, 300].map((deg) => {
            const r = 92;
            const rad = (deg * Math.PI) / 180;
            const cx = 420 + r * Math.cos(rad);
            const cy = 140 + r * Math.sin(rad);
            return <circle key={deg} cx={cx} cy={cy} r="3" fill="#f6dca0" />;
          })}
        </g>

        {/* Spark when wrong attempt */}
        {attempt > 0 && (
          <g key={attempt} className="cuff-shake-spark">
            <circle cx="420" cy="140" r="44" fill="none" stroke="#ff4d3a" strokeWidth="2" opacity="0.7" />
          </g>
        )}
      </svg>

      <style>{`
        @keyframes cuffSparkAnim {
          0% { opacity: 0; transform: scale(0.6); }
          40% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        .cuff-shake-spark { transform-origin: 420px 140px; animation: cuffSparkAnim 520ms ease-out forwards; }
      `}</style>
    </div>
  );
}
